import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { BlueprintGrid, CrossMarker } from "../components/BlueprintGrid";
import { lineMask, EASE, fadeUp, viewportOnce } from "../lib/motion";
import { HERO_IMAGES } from "../lib/product";
import { WireframeHouse } from "./product/shared";
import { trackEvent } from "../lib/seo";
import { LOCATION_COORDINATE_META } from "../lib/site";

const LINES = ["BUILD WITH", "CLARITY.", "NOT", "GUESSWORK."];

const Hero = () => (
    <section
        data-testid="hero"
        className="relative overflow-hidden bg-ink"
    >
        <BlueprintGrid />
        <CrossMarker className="absolute right-10 top-32 hidden text-white/25 md:block" />

        <div className="relative z-10 mx-auto grid max-w-[1600px] items-center gap-12 px-6 pb-20 pt-[128px] md:px-10 lg:grid-cols-12 lg:px-16 lg:pt-[168px]">
            <div className="lg:col-span-6">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                    className="flex items-center gap-3 font-mono text-[13px] leading-relaxed tracking-[0.3em] text-mist"
                >
                    <span className="h-1.5 w-1.5 shrink-0 bg-accent" />
                    SOUTH AFRICA'S RESIDENTIAL CONSTRUCTION INTELLIGENCE
                    PLATFORM
                </motion.p>

                <h1
                    data-testid="hero-headline"
                    className="mt-8 font-display text-[clamp(2.3rem,7.5vw,7rem)] font-bold leading-[1.02] text-white"
                >
                    {LINES.map((line, i) => (
                        <span
                            key={line}
                            className="-mb-[0.08em] block overflow-hidden pb-[0.08em]"
                        >
                            <motion.span
                                className="block"
                                variants={lineMask}
                                initial="hidden"
                                animate="show"
                                custom={i}
                            >
                                {i === 1 ? (
                                    <>
                                        CLARITY
                                        <span className="text-accent">.</span>
                                    </>
                                ) : i === 3 ? (
                                    <>
                                        GUESSWORK
                                        <span className="text-accent">.</span>
                                    </>
                                ) : (
                                    line
                                )}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
                    className="mt-8 max-w-xl text-[16px] leading-relaxed text-mist md:text-lg"
                >
                    Building a home shouldn't mean navigating complicated
                    construction information, uncertain costs and contractor
                    quotations alone. BuildEase brings construction
                    intelligence, cost insights and digital visualisation
                    together to help South African homeowners make more
                    informed building decisions.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <Link
                        to="/join-the-pilot"
                        data-testid="hero-start-build"
                        onClick={() => trackEvent("start_build_click", "hero")}
                        className="group inline-flex items-center gap-2 bg-accent px-7 py-4 text-[16px] font-semibold text-white transition-colors duration-300 hover:bg-[#ff7026]"
                    >
                        Start Your Build
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </Link>
                    <Link
                        to="/product"
                        data-testid="hero-explore-platform"
                        className="inline-flex items-center gap-2.5 border border-white/25 px-7 py-4 text-[16px] font-medium text-white transition-colors duration-300 hover:border-accent"
                    >
                        <Play className="h-3.5 w-3.5" />
                        Explore BuildEase
                    </Link>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: EASE }}
                className="relative lg:col-span-6"
            >
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    data-testid="hero-visual"
                    className="relative border border-white/12"
                >
                    <div className="relative overflow-hidden">
                        <img
                            src={HERO_IMAGES.house3d}
                            alt="Contemporary double-storey South African home with large glazing at dusk, shown with its architectural wireframe overlay"
                            className="aspect-[4/3] w-full object-cover"
                        />
                        <WireframeHouse className="pointer-events-none absolute bottom-6 left-6 w-[46%] text-white/50" />
                        <span className="absolute right-4 top-4 border border-white/30 bg-[#0A0F1D]/70 px-2.5 py-1 font-mono text-[13px] tracking-[0.22em] text-white/90 backdrop-blur-sm">
                            PLAN → BUILD
                        </span>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 px-5 py-3.5">
                        <span className="font-mono text-[13px] tracking-[0.22em] text-white">
                            FIG. 01 — FROM PLAN TO HOME
                        </span>
                        <span className="font-mono text-[13px] tracking-[0.22em] text-cad">
                            DURBAN, KZN
                        </span>
                    </div>
                </motion.div>
            </motion.div>
        </div>

        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="relative z-10 border-t border-white/10"
            data-testid="hero-metadata-bar"
        >
            <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-6 px-6 py-4 font-mono text-[13px] tracking-[0.24em] text-mist/80 md:px-10 lg:px-16">
                <span className="hidden sm:block">BUILD INTELLIGENCE</span>
                <span>SIMPLIFY. BUILD. LIVE.</span>
                <span>SOUTH AFRICA</span>
                <span className="hidden md:block">{LOCATION_COORDINATE_META}</span>
            </div>
        </motion.div>
    </section>
);

export default Hero;
